import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect, lazy, Suspense } from 'react'
import { supabase, formatErrorMessage } from './lib/supabase'
import { ToastProvider } from './contexts/ToastContext'
import { AudioProcessingProvider } from './contexts/AudioProcessingContext'
import { VoiceProvider } from './contexts/VoiceContext'
import { ErrorBoundary } from './components/ErrorBoundary'

// Critical pages (loaded immediately)
import LandingPage from './pages/LandingPage'
import MainApp from './pages/MainApp'
import AuthCallback from './pages/AuthCallback'

// Lazy-loaded pages (code-split for smaller initial bundle)
const SignUpPage = lazy(() => import('./pages/SignUpPage'))
const VoiceTestPage = lazy(() => import('./pages/VoiceTestPage'))
const VideoTestPage = lazy(() => import('./pages/VideoTestPage'))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))
const FullProfilePage = lazy(() => import('./pages/FullProfilePage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const DevLoginPage = lazy(() => import('./pages/DevLoginPage'))

// Mobile Layout
import MobileLayout from './components/mobile/MobileLayout'
import useDeviceType from './hooks/useDeviceType'
import FloatingHUDLayout from './components/FloatingHUDLayout'

// Sound-Toast bridge
import SoundToastBridge from './components/SoundToastBridge'

// Gil Voice Assistant
import GilAssistant from './components/gil/GilAssistant'

/**
 * Detect if user is on a mobile device using user agent
 * More reliable than viewport width for redirect purposes
 */
function isMobileDevice() {
  if (typeof navigator === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

/**
 * Check if we should redirect to mobile subdomain
 * DISABLED: m.thegjyl.com is not deployed, use main domain for all devices
 */
function shouldRedirectToMobile() {
  // Mobile subdomain redirect disabled - use responsive design on main domain
  return false
}

function App() {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const { isMobile, isClient } = useDeviceType()

  useEffect(() => {
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchProfile(session.user.id)
      } else {
        setLoading(false)
      }
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchProfile(session.user.id)
      } else {
        setProfile(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Listen for profile updates from EnhancedUserProfile modal
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const updatedProfile = event.detail
      if (updatedProfile) {
        // Update profile state directly with the updated data
        setProfile(prev => ({ ...prev, ...updatedProfile }))
      }
    }

    window.addEventListener('profile-updated', handleProfileUpdate)
    return () => window.removeEventListener('profile-updated', handleProfileUpdate)
  }, [])

  // DFN3 noise suppression (23MB WASM) is loaded ON-DEMAND only
  // It will be dynamically imported when user clicks on voice/video settings
  // This prevents the 1-minute mobile load time from downloading 23MB on every page load

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) throw error
      setProfile(data)
    } catch (error) {
      console.error('Error fetching profile:', formatErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  // Show loading state while loading auth or waiting for client
  if (loading || !isClient) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-blue-950">
        {/* Logo */}
        <img
          src="/logos/gjyl-logo-oauth-512.png"
          alt="GJYL"
          className="w-20 h-20 mb-6 rounded-xl drop-shadow-2xl animate-pulse"
        />
        {/* Spinner */}
        <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-4"></div>
        {/* Text */}
        <div className="text-slate-400 text-sm font-medium">Loading...</div>
      </div>
    )
  }

  // Determine which app to show based on device type
  const AppComponent = isMobile ? MobileLayout : MainApp

  return (
    <ErrorBoundary>
      <ToastProvider>
        <SoundToastBridge />
        <AudioProcessingProvider>
          <VoiceProvider>
          <Router>
            {/* Gil Voice Assistant - shows for logged-in users */}
            {user && profile && <GilAssistant userName={profile?.display_name || profile?.username} />}
            <Suspense fallback={<div className="flex items-center justify-center h-screen bg-[#0a1628]"><div className="animate-pulse text-white">Loading...</div></div>}>
            <Routes>
              <Route
                path="/"
                element={user && profile ? <AppComponent user={user} profile={profile} /> : <LandingPage />}
              />
              <Route
                path="/signup"
                element={user && profile ? <Navigate to="/app" replace /> : <SignUpPage />}
              />
              <Route
                path="/app/*"
                element={user && profile ? <AppComponent user={user} profile={profile} /> : <Navigate to="/" replace />}
              />
              <Route path="/landing" element={<LandingPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/dev-login" element={<DevLoginPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/voice-test" element={<VoiceTestPage />} />
              <Route path="/video-test" element={<VideoTestPage />} />
              <Route path="/hud" element={<FloatingHUDLayout />} />
              <Route
                path="/profile/:profileId"
                element={user && profile ? <FullProfilePage userId={user.id} currentUserProfile={profile} /> : <Navigate to="/" replace />}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </Router>
          </VoiceProvider>
        </AudioProcessingProvider>
      </ToastProvider>
    </ErrorBoundary>
  )
}

export default App
